import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
// import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase-shim';
// import { db } from '../firebase/config';
import { useAuth } from './AuthContext';
import useFirstTimeUser from '../hooks/useFirstTimeUser';
import indexedDBService from '../utils/indexedDBService';

interface SetupGuideContextType {
  isSetupComplete: boolean;
  isSetupOpen: boolean;
  openSetupGuide: () => void;
  closeSetupGuide: () => void;
  checkSetupStatus: () => Promise<boolean>;
  resetSetupGuide: () => Promise<void>;
}

const SetupGuideContext = createContext<SetupGuideContextType | null>(null);

export const useSetupGuide = () => {
  const context = useContext(SetupGuideContext);
  if (!context) {
    throw new Error('useSetupGuide must be used within a SetupGuideProvider');
  }
  return context;
};

interface SetupGuideProviderProps {
  children: ReactNode;
}

export const SetupGuideProvider: React.FC<SetupGuideProviderProps> = ({ children }) => {
  const { currentUser, userProfile } = useAuth();
  const { isFirstTimeUser } = useFirstTimeUser();
  const [isSetupComplete, setIsSetupComplete] = useState(false);
  const [isSetupOpen, setIsSetupOpen] = useState(false);

  // Check if the user has completed all setup steps
  const checkSetupStatus = async (): Promise<boolean> => {
    if (!currentUser || !userProfile) {
      return false;
    }

    try {
      // First check if setup was already marked as complete in the user profile
      // Use IndexedDB profile check
      const profile = await indexedDBService.getItemById<any>('user_profiles', currentUser.uid);
      if (profile && profile.setupCompleted === true) {
        setIsSetupComplete(true);
        return true;
      }

      // Check cash balance
      const cashBalanceSet = userProfile?.accountSettings?.openingCashBalance !== undefined;

      // Check bank accounts
      const bankAccountAdded = userProfile?.accountSettings?.openingBankBalances
        && userProfile?.accountSettings?.openingBankBalances.length > 0;

      // Check inventory items
      let inventoryItemAdded = false;
      try {
        const inventory = await indexedDBService.getAllItems<any>('inventory');
        inventoryItemAdded = inventory.some(item => item.userId === currentUser.uid);
      } catch (e) { console.error(e); }

      // Check clients
      let clientAdded = false;
      try {
        const clients = await indexedDBService.getAllItems<any>('clients');
        clientAdded = clients.some(client => client.userId === currentUser.uid);
      } catch (e) { console.error(e); }

      // Check vendors
      let vendorAdded = false;
      try {
        const vendors = await indexedDBService.getAllItems<any>('vendors');
        vendorAdded = vendors.some(vendor => vendor.userId === currentUser.uid);
      } catch (e) { console.error(e); }

      // Check if profile is complete
      const isProfileComplete =
        !!userProfile.displayName &&
        !!userProfile.phone &&
        !!userProfile.businessName &&
        !!userProfile.address;

      // Setup is complete if user has completed all steps
      const setupComplete = !!(cashBalanceSet &&
        bankAccountAdded &&
        inventoryItemAdded &&
        clientAdded &&
        vendorAdded &&
        isProfileComplete);

      setIsSetupComplete(setupComplete);

      // If setup is now complete, mark it as completed permanently in the database
      if (setupComplete) {
        await indexedDBService.putItem('user_profiles', {
          ...profile,
          setupCompleted: true
        });
        setIsSetupOpen(false);
      } else {
        // Check if user has permanently skipped the setup guide
        const permanentlySkipped = localStorage.getItem('setup_guide_permanently_skipped') === 'true';

        if (permanentlySkipped) {
          // User has permanently skipped, don't show again
          return setupComplete;
        }

        // Show setup guide for new users who are missing required setup items
        // but only if setup wasn't previously marked as complete
        // AND only if it hasn't been dismissed recently
        const dismissed = sessionStorage.getItem('setup_guide_dismissed');
        const lastDismissed = dismissed ? parseInt(dismissed) : 0;
        const now = Date.now();
        const oneDayInMs = 24 * 60 * 60 * 1000; // 24 hours

        // Only auto-open if:
        // 1. User is first time user
        // 2. Setup is not complete
        // 3. Not permanently skipped
        // 4. Not dismissed in last 24 hours
        if (isFirstTimeUser && !setupComplete && currentUser && (now - lastDismissed > oneDayInMs)) {
          // Only show if profile is incomplete OR setup is incomplete
          if (!isProfileComplete || !setupComplete) {
            setIsSetupOpen(true);
          }
        }
      }

      return setupComplete;
    } catch (error) {
      console.error('Error checking setup status:', error);
      return false;
    }
  };

  // Reset setup guide for testing or admin purposes
  const resetSetupGuide = async (): Promise<void> => {
    if (!currentUser) return;

    try {
      // Fetch current profile first to preserve other fields
      const profile = await indexedDBService.getItemById<any>('user_profiles', currentUser.uid);
      if (profile) {
        await indexedDBService.putItem('user_profiles', {
          ...profile,
          setupCompleted: false
        });
      }
      setIsSetupComplete(false);
      // Don't automatically open after reset
    } catch (error) {
      console.error('Error resetting setup guide:', error);
    }
  };

  useEffect(() => {
    // Check setup status when user or profile changes
    if (currentUser && userProfile) {
      checkSetupStatus();
    }
  }, [currentUser, userProfile]);

  const openSetupGuide = () => {
    setIsSetupOpen(true);
  };

  const closeSetupGuide = () => {
    setIsSetupOpen(false);
    // Mark as dismissed for 24 hours
    sessionStorage.setItem('setup_guide_dismissed', Date.now().toString());
  };

  return (
    <SetupGuideContext.Provider
      value={{
        isSetupComplete,
        isSetupOpen,
        openSetupGuide,
        closeSetupGuide,
        checkSetupStatus,
        resetSetupGuide,
      }}
    >
      {children}
    </SetupGuideContext.Provider>
  );
};

export default SetupGuideProvider; 
